<template>
  <div>
    <page-content-toolbar
      :projectName="`${project && project.name ? project.name : ' '}`"
      :title="$t('sourcesAndDeployments.deploymentDetails')"
    />
    <v-container
      v-if="dataLoaded"
      :class="'px-12 pt-7 pb-1 mt-2 ' + (deployment ? '' : 'grey--text')"
      fluid
    >
      <v-row justify="start">
        <v-col cols="6" md="4" lg="3" sm="12" class="py-1 my-1 black--text">
          <h3>{{ $t('sourcesAndDeployments.deploymentDetails') }}</h3>
        </v-col>
        <v-col cols="6" md="4" lg="9" sm="12" class="py-1 my-1">
          <raison-button
            v-if="deployment && deployment.status === $options.DEPLOYMENT_STATUS_ONLINE"
            :button-label="$t('sourcesAndDeployments.undeploy')"
            :button-function="undeploy"
            :loading="deploymentLoading"
          >
            <template v-slot:prepend>
              <v-icon left>mdi-airplane-landing</v-icon>
            </template>
          </raison-button>
          <raison-button
            v-if="deployment && ( deployment.status === $options.DEPLOYMENT_STATUS_NOT_DEPLOYED || deployment.status === $options.DEPLOYMENT_STATUS_OFFLINE || deployment.status === $options.DEPLOYMENT_STATUS_FAILED )"
            :button-label="$t('sourcesAndDeployments.deploy')"
            :button-function="deploy"
            :loading="deploymentLoading"
            :disabled="!modelStage || !testVersion(modelStage.text) || (testVersion(modelStage.text) && displayStageName !== '')"
          >
            <template v-slot:prepend>
              <v-icon left>mdi-airplane-takeoff</v-icon>
            </template>
          </raison-button>
        </v-col>
      </v-row>
        <v-row justify="start">
          <v-col cols="3" sm="12" md="4" lg="3" class="py-1 my-1">
            <b>{{ $t('sourcesAndDeployments.deploymentStatus') }}:</b>
          </v-col>
          <v-col cols="6" md="6" lg="9" class="py-1 my-1">
            <v-chip
              label
              class="ma-2"
              :color="color"
            >
            {{ deployment ? $t('sourcesAndDeployments.status.' + deployment.status) : $t('sourcesAndDeployments.status.undefined') }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row justify="start">
          <v-col cols="3" sm="12" md="4" lg="3" class="py-1 my-1">
            <b>{{ $t('sourcesAndDeployments.deploymentStage') }}:</b>
          </v-col>
          <v-col cols="6" md="6" lg="3" class="py-1 my-1">
            <v-combobox
              v-if="deployment && ( deployment.status === $options.DEPLOYMENT_STATUS_NOT_DEPLOYED  || deployment.status === $options. DEPLOYMENT_STATUS_OFFLINE  || deployment.status === $options. DEPLOYMENT_STATUS_FAILED  )"
              required
              v-model="modelStage"
              :items="availableStages"
              :hint="info"
              hide-selected
              chips
              clearable
              hide-no-data
              :rules="[required,pattern]"
              counter
              maxlength="20"
              @update:search-input="changeStage($event)"
              >
            </v-combobox>    
            <span v-else>
              {{ (deployment && deployment.stage) ? deployment.stage : '' }}
            </span>
          </v-col>
        </v-row>
      
        <v-row justify="start">
          <v-col cols="3" sm="12" md="4" lg="3" class="py-1 my-1">
            <b>{{ $t('sourcesAndDeployments.sourceVersion') }}:</b>
          </v-col>
          <v-col cols="6" md="6" lg="9" class="py-1 my-1">
            {{ deployment ? deployment.version : ''}}
          </v-col>
        </v-row>
        <v-row justify="start">
          <v-col cols="3" sm="12" md="4" lg="3" class="py-1 my-1">
            <b>{{ $t('sourcesAndDeployments.apiAddress') }}:</b>
          </v-col>
          <v-col cols="6" md="6" lg="9" class="py-1 my-1">
            {{ deployment ? deployment.address : '' }}
          </v-col>
        </v-row>
        <v-row justify="start">
          <v-col cols="3" sm="12" md="4" lg="3" class="py-1 my-1">
            <b>{{ $t('sourcesAndDeployments.apiKeyHeaderName') }}:</b>
          </v-col>
          <v-col cols="6" md="6" lg="9" class="py-1 my-1">
            {{ deployment ? deployment.apiKeyName : ''}}
          </v-col>
        </v-row>
        <v-row justify="start">
          <v-col cols="3" sm="12" md="4" lg="3" class="py-1 my-1">
            <b>{{ $t('sourcesAndDeployments.apiKeySecret') }}:</b>
          </v-col>
          <v-col cols="6" md="6" lg="9" class="py-1 my-1">
            {{ deployment ? deployment.apiKeySecret : '' }}
          </v-col>
        </v-row>
        <v-row justify="start">
          <v-col cols="3" sm="12" md="4" lg="3" class="py-1 my-1">
            <b>{{ $t('sourcesAndDeployments.apiSpecifications') }}:</b>
          </v-col>
          <v-col cols="6" md="6" lg="9" class="py-1 my-1">
            <a href="/api-doc_25092020.yaml" download>[OPENAPI]</a>
          </v-col>
        </v-row>
    </v-container>
    <v-progress-circular
      v-else
      size="180"
      width="12"
      class="d-block mx-auto"
      indeterminate
      color="primary"
    />
  </div>
</template>

<script>
import PageContentToolbar from '@/components/layout/PageContentToolbar';
import RaisonButton from '@/components/global/RaisonButton';

import deploymentService from '@/services/deployments.js';
import sourceService from '@/services/sources.js';

import projectPageMixin from '@/mixins/project-page-mixin.js';
import userRightMixin from '@/mixins/user-right-mixin.js';

import { testVersion } from '@/utils/utils.js';


import { DEPLOYMENT_STATUS_ONLINE,
  DEPLOYMENT_STATUS_OFFLINE,
  DEPLOYMENT_STATUS_PENDING,
  DEPLOYMENT_STATUS_ASKED,
  DEPLOYMENT_STATUS_FAILED,
  DEPLOYMENT_STATUS_NOT_DEPLOYED,
} from '@/config/constants.js';

export default {
  name: 'DeploymentDetails',
  
  components: { PageContentToolbar, RaisonButton },

  mixins: [ projectPageMixin, userRightMixin ],

  data () {
    return {
      displayStageName: '',
      deployment: {},
      availableStages: [],
      deploymentLoading: false,
      useNewDeploymentStage: false,
      newDeploymentStage: '',
      deploymentInterval: undefined,
      dataLoaded: false, // because of complexity of various workflows to access this component, we'll rely on this field instead of data validation to show elements to avoid clipping effect
      search: null,
      editing: null,
      index: -1,
      modelStage: '',
      info: null,
    };
  },

  watch: {
    modelStage(val,prev) {
      if(!val) return
      if(val.value && prev && prev.value && val.value.stage === prev.value.stage) return  
      
      let selection = {}
      if(typeof val === 'string') {
        selection = {
          text: val,
          value: { stage: val, version: this.deployment.version },
        };
        this.info = null;
      }
      else {
        selection = {
          text: val.value.stage,
          value: { stage: val.value.stage, version: val.value.version }, 
        };
        this.info = this.$t('sourcesAndDeployments.warnDeploymentOverride', {version: selection.value.version, stage: selection.value.stage})
      }
      this.modelStage = selection;
      this.deployment.stage = selection.value.stage;
    },
  },

  computed: {
    newDeployment () {
      return !!this.$route.params.sourceId;
    },

    color () {
      const status = this.deployment && this.deployment.status || undefined;
      return deploymentService.statusColor(status);
    },
  },

  // add constants to component to make it available in template
  DEPLOYMENT_STATUS_ONLINE,
  DEPLOYMENT_STATUS_OFFLINE,
  DEPLOYMENT_STATUS_PENDING,
  DEPLOYMENT_STATUS_ASKED,
  DEPLOYMENT_STATUS_FAILED,

  methods: {
    testVersion: testVersion,

    deploy () {
      this.deploymentLoading = true;
      let payload = this.deployment;
      this.autoRefresh();
      deploymentService.deploy(this.$route.params.id, payload).then(res => {
        if (this.newDeployment) {
          this.$router.replace({name: 'deploymentDetails', params: { id: this.$route.params.id, deploymentId: res.data.id } });
          this.initDeploymentDetails();
        } else {
          this.getDeployment();
          this.$emit('raison-event', { type: 'success', messages: [this.$t('sourcesAndDeployments.deployedSuccessfully')] });
        }
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] });
        this.deploymentLoading = false;
      })
    },

    undeploy () {
      this.deploymentLoading = true;
      deploymentService.undeploy(this.$route.params.id, this.deployment.id).then(() => {
        this.getDeployment();
        this.$emit('raison-event', { type: 'success', messages: [this.$t('sourcesAndDeployments.undeployedSuccessfully')] });
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] });
      }).finally(() => {
        this.deploymentLoading = false;
      });
    },

    getDeployment() {
      return new Promise ((resolve, reject) => {
        const projectId = this.$route.params.id;
        const deploymentId = this.$route.params.deploymentId;
        if(!deploymentId) {
          return ;
        }
        deploymentService.getDeployment(projectId, deploymentId).then(response => {
          if(response && response.data) {
            this.deployment = response.data;
            const stageObject = {
              "version": this.deployment.version,
              "stage": this.deployment.stage,
            };
            this.stage = stageObject;
          }
          resolve();
        })
        .catch(error => {
          this.$emit('raison-event', { type: 'error', messages: [error] });
          reject();
        }).finally(() => {
          this.deploymentLoading = false;
          this.dataLoaded = true;
        });
      });
    },

    getAvailableStages() {
      deploymentService.getAvailableStages(this.$route.params.id).then(res => {
        this.availableStages.push(...res.data.map(value => {
          return {
            text: value.stage + ' [version: '+value.version+']',
            value: value,
          }
        }));
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] });
      }).finally(() => {
        this.dataLoaded = true;
      });
    },

    getSource() {
      sourceService.getSource(this.$route.params.id, this.$route.params.sourceId).then(res => {
        this.$set(this.deployment, 'version', res.data.version);
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] });
      });
    },

    initNewDeployment() {
      this.getSource();
      this.getAvailableStages();
      this.useNewDeploymentStage = true;
    },

    initDeploymentDetails() {
      this.getDeployment().then(() => {
          if(this.deployment.status === DEPLOYMENT_STATUS_ASKED  || this.deployment.status === DEPLOYMENT_STATUS_PENDING) {
            this.autoRefresh();
          }
          else if(this.deployment.status === DEPLOYMENT_STATUS_NOT_DEPLOYED  || this.deployment.status === DEPLOYMENT_STATUS_OFFLINE) {
            this.getAvailableStages();
          }
        }
      ).finally(() => {
        this.dataLoaded = true;
      })
    },

    autoRefresh() {
      this.deploymentInterval = window.setInterval(() => {
        this.getDeployment();
      }, 5000);
    },
    required(value) {
      if (value instanceof Array && value.length === 0) {
        return this.$t('global.required');
      }
      return !!value || this.$t('global.required');
    },
    pattern(value) {
      if (value instanceof Array && value.length > 0) {
        return testVersion(value[0].text) 
          || this.$t('uploadSource.rule.versionMustMatchPattern');
      } 
      else {
        return (!value || testVersion(value.text))
          || this.$t('uploadSource.rule.versionMustMatchPattern');
      }
    },
    changeStage(event) {
      this.displayStageName = event || '';
    },
  },

  created () {
    if (!this.isUserParameterizer) {
      this.$router.push({name: 'dashboard'});
    }

    if (this.newDeployment) {
      this.initNewDeployment();
    } else {
      this.initDeploymentDetails();
    }
  },

  destroyed() {
    if(this.deploymentInterval) {
      clearInterval(this.deploymentInterval);
    }
  },
};
</script>

<style>
.rai-deployment-detail__input {
  width: 250px;
  display: inline-block;
  padding-top: 0px;
  margin-top: 0px;
  margin-bottom: 0px;
  padding-bottom: 0px;
  height: 57px;
}
</style>
